import { useMemo } from 'react'

import { buildSuggestedDatesParamsKey, getShouldUseSearchPrices } from 'lib/search/searchUtils'
import { getCanViewLuxPlusBenefits, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { getSuggestedDates } from 'selectors/offerSelectors'
import { getSumExtraGuestsSurcharge } from 'lib/checkout/cartReservationUtils'
import { reportLog } from '../../services/userMonitoring'
import { useAppSelector } from 'hooks/reduxHooks'
import useOfferMetaData from '../Offers/useOfferMetaData'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { determineMemberPricing, onlyNonZeroFees } from 'lib/offer/pricing'
import { isBundlePackageWithPrice } from 'lib/offer/offerTypes'
import resolveLuxPlusMemberPrice from 'lib/search/resolveLuxPlusMemberPrice'
import useAnytimePricePresentation from '../Offers/useAnytimePricePresentation'
import { getAccommodationSearchProductType } from 'lib/search/accommodationSearchPresentation'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'

interface Props {
  offer: App.Offer | App.OfferSummary
  pkg?: App.Package | App.BundlePackageWithPrice
  rate?: App.OfferAvailableRate
  /** Length of stay the caller labels the price with, in nights. */
  duration?: number
  filters?: App.OfferListFilters
  useRealSearchPrices?: boolean // use prices directly from search api
  showPerNightPricing?: boolean
  /**
   * The product type the tile is presenting the offer as. Defaults to search's own, so a component
   * that doesn't pass one can't disagree with the tile around it about whether a price is shown.
   */
  productType?: App.ProductType
}

interface UseSearchPricesResult {
  /** @deprecated - use defaultPricing` instead */
  totalPrice: number
  /** @deprecated - use memberPricing instead */
  totalMemberPrice?: number
  /** @deprecated - use defaultPricing instead */
  totalValueBase: number
  /** @deprecated - use defaultPricing` instead */
  discountPercent: number
  hotelPrice: number
  hotelMemberPrice: number
  showMemberPrice: boolean
  propertyFees: number
  suggestedDates?: App.OfferSuggestedDates
  defaultPricing: App.PricingWithValue
  memberPricing?: App.PricingWithValue
  /**
   * Whether search should show this price at all. Follows the shared accommodation search policy,
   * including the dynamic Flash campaign promotion that keeps an anytime price visible.
   */
  hidePrice: boolean
}

type SearchPricing = Omit<UseSearchPricesResult, 'hidePrice'>

export function useDirectSearchPrices({ filters, offerId }: { filters: App.OfferListFilters, offerId: string }): App.OfferListMetadataPricing | undefined {
  const offerMetadata = useOfferMetaData(offerId, filters)
  if (getShouldUseSearchPrices(filters)) return offerMetadata?.pricing
}

function calculatePerNightPrice(price: number, duration: number = 1) {
  return Math.round(price / duration)
}

interface AnytimeSearchPricingOptions {
  saleUnit: string
  showPerNightPricing: boolean
  canShowMemberPrice: boolean
}

/**
 * Prices a tile from the property's anytime promotion rate.
 *
 * `displayPrice` is already inclusive of taxes, fees and property fees, matching what the package
 * and metadata paths total up before display.
 */
function buildAnytimeSearchPricing(
  anytimePromotionRate: App.Property.AnytimePromotionRate,
  { saleUnit, showPerNightPricing, canShowMemberPrice }: AnytimeSearchPricingOptions,
): SearchPricing | undefined {
  const { totals, luxPlusTotals, duration } = anytimePromotionRate
  if (!totals?.displayPrice) return undefined

  const perNight = (price: number = 0) => showPerNightPricing ? calculatePerNightPrice(price, duration) : price

  const price = perNight(totals.displayPrice)
  const value = perNight(totals.valuedUpToPrice)
  const memberPrice = canShowMemberPrice && luxPlusTotals?.displayPrice
    ? perNight(luxPlusTotals.displayPrice)
    : undefined
  const propertyFees = perNight(totals.propertyFees)

  const defaultPricing: App.PricingWithValue = {
    price,
    value,
    discountPercent: getValueOffPercent(value, price),
    saleUnit,
    fees: onlyNonZeroFees([
      { type: 'property', amount: propertyFees },
      { type: 'taxesAndFees', amount: perNight(totals.taxesAndFees) },
    ]),
  }

  return {
    totalPrice: price,
    totalMemberPrice: memberPrice,
    totalValueBase: value,
    discountPercent: defaultPricing.discountPercent,
    hotelPrice: price - propertyFees,
    hotelMemberPrice: memberPrice ? memberPrice - propertyFees : 0,
    showMemberPrice: !!memberPrice,
    propertyFees,
    defaultPricing,
    memberPricing: determineMemberPricing(defaultPricing, memberPrice, value),
  }
}

export function useSearchPrices({ offer, pkg, rate, duration, filters, useRealSearchPrices = false, showPerNightPricing = false, productType }: Props): UseSearchPricesResult {
  const luxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const offerMetadata = useOfferMetaData(offer.id, filters)
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const flexibleSearchFilterKey = buildSuggestedDatesParamsKey(filters?.flexibleMonths, filters?.flexibleNights, filters?.rooms)
  const suggestedDates = useAppSelector((state) => getSuggestedDates(state, flexibleSearchFilterKey, offer.id))
  const isFlexibleSearch = filters?.flexibleMonths || filters?.flexibleNights

  // The shared hide policy exempts a limited time special at standard occupancy, which is what
  // search presents a tactical offer as — so this has to be search's product type, not the raw one.
  const searchProductType = productType ?? getAccommodationSearchProductType(
    'productType' in offer ? offer.productType : undefined,
    isLPCTacticalEnabled() && 'hasTactical' in offer && !!offer.hasTactical,
  )
  const { hidePrice, anytimePromotionRate } = useAnytimePricePresentation({
    offerType: offer.type,
    productType: searchProductType,
    metadataHidePricing: offerMetadata?.hidePricing,
    filters,
    accommodationPropertyId: offerMetadata?.accommodationId,
    displayedDuration: duration,
  })

  const rateOrPkg = (rate ?? pkg)
  const baseMemberPrice = rateOrPkg?.memberPrice || 0
  const showMemberPrice = luxPlusEnabled && baseMemberPrice > 0
  const rateOrPkgMemberPrice = rate?.memberPriceWithSurcharge ?? pkg?.memberPrice
  const rooms = useMemo(() => filters?.rooms || [], [filters?.rooms])

  const priceMultiplier = rate ? 1 : (rooms.length || 1)
  const propertyFees = (rate?.propertyFees ?? pkg?.propertyFees ?? 0) * priceMultiplier
  const extraGuestSurcharges = useMemo(() => {
    if (!pkg) return 0

    if (isBundlePackageWithPrice(pkg)) {
      return 0
    }
    return getSumExtraGuestsSurcharge(rooms, offer, pkg)
  }, [pkg, rooms, offer])
  const hotelPrice = ((rateOrPkg?.price ?? 0) + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges
  const hotelMemberPrice = showMemberPrice ? ((rateOrPkgMemberPrice ?? 0 + (pkg?.surcharge ?? 0)) * priceMultiplier + extraGuestSurcharges) : 0
  const totalPrice = isFlexibleSearch && suggestedDates?.price ? suggestedDates?.price : hotelPrice + propertyFees
  const pkgValueOrMemberValue = canViewLuxPlusBenefits ? pkg?.memberValue : pkg?.value
  const totalValueBase = rate?.hotelValue ?? suggestedDates?.value ?? (pkgValueOrMemberValue ?? 0 * (rooms.length || 1) + extraGuestSurcharges)
  const discountPercent = pkg?.discountPercent || getValueOffPercent(totalValueBase, showMemberPrice && canViewLuxPlusBenefits ? hotelMemberPrice : hotelPrice)
  const surcharge = (rateOrPkg?.surcharge ?? 0) * priceMultiplier
  const taxesAndFees = (rateOrPkg?.taxesAndFees ?? 0) * priceMultiplier
  const perNightDuration = useRealSearchPrices
    ? offerMetadata?.pricing?.duration ?? rate?.duration ?? pkg?.duration
    : rate?.duration ?? pkg?.duration

  const totalMemberPrice = useMemo(() => {
    if (showMemberPrice) {
      let memberPrice: number
      if (isFlexibleSearch && suggestedDates?.memberPrice) {
        memberPrice = suggestedDates.memberPrice
      } else {
        memberPrice = hotelMemberPrice + propertyFees
      }
      return memberPrice
    }
  }, [showMemberPrice, isFlexibleSearch, suggestedDates, hotelMemberPrice, propertyFees])

  const resolvedTotalPrice = useMemo(() => {
    return showPerNightPricing ? calculatePerNightPrice(totalPrice, perNightDuration) : totalPrice
  }, [showPerNightPricing, totalPrice, perNightDuration])

  const resolvedTotalValueBase = useMemo(() => {
    return showPerNightPricing ? calculatePerNightPrice(totalValueBase, perNightDuration) : totalValueBase
  }, [showPerNightPricing, totalValueBase, perNightDuration])

  const resolvedTotalMemberPrice = useMemo(() => {
    if (totalMemberPrice === undefined) return undefined
    return showPerNightPricing ? calculatePerNightPrice(totalMemberPrice, perNightDuration) : totalMemberPrice
  }, [showPerNightPricing, totalMemberPrice, perNightDuration])

  const pricing = useMemo<SearchPricing>(() => {
    const fees = onlyNonZeroFees([
      { type: 'property', amount: propertyFees },
      { type: 'taxesAndFees', amount: taxesAndFees },
      { type: 'surcharge', amount: surcharge },
      { type: 'extraGuestSurcharge', amount: extraGuestSurcharges },
    ])

    // A dynamic Flash promotion is the only reason an anytime price is on show, so it has to be
    // that promotion's price — search metadata only knows the hidden "from" price.
    if (anytimePromotionRate) {
      const anytimePricing = buildAnytimeSearchPricing(anytimePromotionRate, {
        saleUnit: offer.saleUnit,
        showPerNightPricing,
        canShowMemberPrice: luxPlusEnabled,
      })
      if (anytimePricing) return anytimePricing
    }

    if (useRealSearchPrices) {
      if (!offerMetadata?.pricing) {
        const fallbackDefaultPricing: App.PricingWithValue = {
          price: resolvedTotalPrice,
          value: resolvedTotalValueBase,
          discountPercent: getValueOffPercent(resolvedTotalValueBase, resolvedTotalPrice),
          saleUnit: offer.saleUnit,
          fees,
        }
        const fallbackMemberPricing = determineMemberPricing(fallbackDefaultPricing, resolvedTotalMemberPrice, resolvedTotalValueBase)

        // If metadata pricing is absent but we already have rate/package pricing, use it.
        if (rate || pkg) {
          return {
            totalPrice: fallbackDefaultPricing.price,
            totalMemberPrice: resolvedTotalMemberPrice,
            totalValueBase: resolvedTotalValueBase,
            discountPercent,
            hotelPrice,
            hotelMemberPrice,
            showMemberPrice,
            propertyFees,
            suggestedDates,
            defaultPricing: fallbackDefaultPricing,
            memberPricing: fallbackMemberPricing,
          }
        }

        reportLog('No pricing found for offer from search', { offerId: offer.id, filters }, 'error')
        return {
          totalPrice: 0,
          totalMemberPrice: 0,
          totalValueBase: 0,
          discountPercent: 0,
          hotelPrice: 0,
          hotelMemberPrice: 0,
          showMemberPrice: false,
          propertyFees: 0,
          suggestedDates,
          defaultPricing: {
            price: 0,
            value: 0,
            discountPercent: 0,
            saleUnit: offer.saleUnit,
            fees: [],
          },
          memberPricing: undefined,
        }
      }

      const fallbackPrice = offer.lowestPricePackage
      const pricing = offerMetadata?.pricing
      const hasExplicitNoMemberPriceSignalFromSearch = pricing.lowestMemberPrice === 0
      const resolvedShowMemberPrice = hasExplicitNoMemberPriceSignalFromSearch ? false : showMemberPrice

      const metadataTotalPrice = pricing.lowestPrice ?? fallbackPrice?.price ?? 0
      const resolvedMemberPrice = resolveLuxPlusMemberPrice({
        metadataLowestMemberPrice: pricing.lowestMemberPrice,
        packagePrice: pkg?.price,
        packageMemberPrice: pkg?.memberPrice,
        referencePrice: metadataTotalPrice,
        fallbackMemberPricingPrice: fallbackPrice?.memberPricing?.price,
        fallbackMemberPrice: fallbackPrice?.memberPrice,
      })
      const metadataTotalMemberPrice = hasExplicitNoMemberPriceSignalFromSearch
        ? 0
        : (resolvedMemberPrice ?? 0)

      const metadataDiscountPercent = (() => {
        if (hasExplicitNoMemberPriceSignalFromSearch) {
          if (pricing.lowestPrice && pricing.lowestPriceValue !== undefined) {
            return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestPrice)
          }
          return getValueOffPercent(fallbackPrice?.value ?? 0, fallbackPrice?.price ?? 0)
        }

        if (resolvedShowMemberPrice && canViewLuxPlusBenefits && metadataTotalMemberPrice > 0 && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, metadataTotalMemberPrice)
        }
        if (pricing.lowestPrice && pricing.lowestPriceValue !== undefined) {
          return getValueOffPercent(pricing.lowestPriceValue, pricing.lowestPrice)
        }
        return getValueOffPercent(fallbackPrice?.value ?? 0, fallbackPrice?.price ?? 0)
      })()
      const metadataTotalValueBase = pricing?.lowestPriceValue ?? fallbackPrice?.value ?? 0
      const resolvedMetadataTotalPrice = showPerNightPricing ? calculatePerNightPrice(metadataTotalPrice, perNightDuration) : metadataTotalPrice
      const resolvedMetadataTotalValueBase = showPerNightPricing
        ? calculatePerNightPrice(metadataTotalValueBase, perNightDuration)
        : metadataTotalValueBase
      const resolvedMetadataTotalMemberPrice = hasExplicitNoMemberPriceSignalFromSearch ?
        0 :
          (showPerNightPricing ? calculatePerNightPrice(metadataTotalMemberPrice, perNightDuration) : metadataTotalMemberPrice)

      const defaultPricing: App.PricingWithValue = {
        price: resolvedMetadataTotalPrice,
        value: resolvedMetadataTotalValueBase,
        discountPercent: getValueOffPercent(resolvedMetadataTotalValueBase, resolvedMetadataTotalPrice),
        saleUnit: offer.saleUnit,
        fees,
      }
      const memberPricingResolved = determineMemberPricing(defaultPricing, resolvedMetadataTotalMemberPrice, resolvedMetadataTotalValueBase)

      return {
        totalPrice: resolvedMetadataTotalPrice,
        totalMemberPrice: resolvedMetadataTotalMemberPrice,
        totalValueBase: resolvedMetadataTotalValueBase,
        discountPercent: metadataDiscountPercent,
        hotelPrice: pricing.lowestPrice ?? fallbackPrice?.price ?? 0,
        hotelMemberPrice: hasExplicitNoMemberPriceSignalFromSearch ? 0 : (resolvedMemberPrice ?? 0),
        showMemberPrice: resolvedShowMemberPrice,
        propertyFees: fallbackPrice?.propertyFees ?? 0,
        suggestedDates,
        defaultPricing,
        memberPricing: hasExplicitNoMemberPriceSignalFromSearch ? undefined : memberPricingResolved,
      }
    }

    const defaultPricing: App.PricingWithValue = {
      price: resolvedTotalPrice,
      value: resolvedTotalValueBase,
      discountPercent: getValueOffPercent(resolvedTotalValueBase, resolvedTotalPrice),
      saleUnit: offer.saleUnit,
      fees,
    }

    return {
      totalPrice: resolvedTotalPrice,
      totalMemberPrice: resolvedTotalMemberPrice,
      totalValueBase: resolvedTotalValueBase,
      discountPercent,
      hotelPrice,
      hotelMemberPrice,
      showMemberPrice,
      propertyFees,
      suggestedDates,
      defaultPricing,
      memberPricing: determineMemberPricing(defaultPricing, resolvedTotalMemberPrice, resolvedTotalValueBase),
    }
  }, [propertyFees, taxesAndFees, surcharge, extraGuestSurcharges, useRealSearchPrices, resolvedTotalPrice, resolvedTotalValueBase, pkg, offer, resolvedTotalMemberPrice, discountPercent, hotelPrice, hotelMemberPrice, showMemberPrice, suggestedDates, offerMetadata?.pricing, filters, rate, canViewLuxPlusBenefits, showPerNightPricing, perNightDuration, anytimePromotionRate, luxPlusEnabled])

  return useMemo<UseSearchPricesResult>(() => ({ ...pricing, hidePrice }), [pricing, hidePrice])
}
